import React, { useEffect, useState } from 'react';
import { CheckCircle, Clock, QrCode, ShieldCheck, Ticket, XCircle } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from './Header';
import { getFullImageUrl, getTicketById, markTicketUsed, TicketData } from '../services/api';
import { getCurrentToken } from '../utils/userStorage';

const formatMoney = (value?: number) => new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  maximumFractionDigits: 2
}).format(Number(value || 0));

const formatDate = (value?: string) => {
  if (!value) return 'Fecha a confirmar';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('es-AR', { dateStyle: 'full', timeStyle: 'short' }).format(date);
};

const resolveImageUrl = (value?: string) => {
  if (!value) return '';
  return /^(https?:|data:|blob:)/i.test(value) ? value : getFullImageUrl(value);
};

const TicketView: React.FC = () => {
  const navigate = useNavigate();
  const { ticketId } = useParams();
  const [ticket, setTicket] = useState<TicketData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isMarking, setIsMarking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const loadTicket = async () => {
      if (!ticketId) {
        setError('Entrada no encontrada.');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const data = await getTicketById(ticketId);
        if (active) setTicket(data);
      } catch (requestError: any) {
        if (active) setError(requestError.message || 'No pudimos cargar la entrada.');
      } finally {
        if (active) setIsLoading(false);
      }
    };

    loadTicket();
    return () => { active = false; };
  }, [ticketId]);

  const handleMarkUsed = async () => {
    if (!ticket?.ticketId) return;
    try {
      setIsMarking(true);
      setError(null);
      const updatedTicket = await markTicketUsed(ticket.ticketId);
      setTicket(updatedTicket);
    } catch (requestError: any) {
      setError(requestError.message || 'No pudimos marcar la entrada como usada.');
    } finally {
      setIsMarking(false);
    }
  };

  const statusConfig = ticket?.status === 'used'
    ? { label: 'Usada', icon: CheckCircle, className: 'bg-slate-100 text-slate-700' }
    : ticket?.status === 'active'
      ? { label: 'Activa', icon: ShieldCheck, className: 'bg-[#eff8c9] text-[#536d12]' }
      : { label: 'No disponible', icon: XCircle, className: 'bg-rose-50 text-rose-700' };
  const StatusIcon = statusConfig.icon;
  const canTryMarkUsed = Boolean(getCurrentToken() && ticket?.status === 'active');

  return (
    <div className="min-h-screen bg-[#f8f5ef] text-slate-950">
      <Header showBackButton onBack={() => navigate('/')} title="Entrada PEEKPASS" />
      <main className="mx-auto grid max-w-5xl gap-6 px-4 py-8 sm:px-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:py-12">
        <section className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-[0_24px_54px_-40px_rgba(15,23,42,0.35)] sm:p-8">
          {isLoading ? (
            <div className="grid min-h-64 place-items-center text-sm font-bold text-slate-500"><Clock className="mb-3 h-7 w-7 animate-spin" />Cargando entrada...</div>
          ) : error && !ticket ? (
            <div className="grid min-h-64 place-items-center text-center">
              <div><XCircle className="mx-auto h-10 w-10 text-rose-600" /><p className="mt-4 text-sm font-bold text-slate-700">{error}</p></div>
            </div>
          ) : ticket ? (
            <>
              <div className="flex flex-col gap-4 border-b border-slate-200 pb-6 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.22em] text-[#71931b]">Entrada</p>
                  <h1 className="mt-2 text-3xl font-black tracking-[-0.06em]">{ticket.eventTitle}</h1>
                  <p className="mt-2 text-sm font-semibold text-slate-600">{ticket.ticketTierName || 'General'}</p>
                </div>
                <span className={`inline-flex w-fit items-center gap-2 rounded-full px-3 py-2 text-xs font-black uppercase tracking-[0.12em] ${statusConfig.className}`}>
                  <StatusIcon className="h-4 w-4" />{statusConfig.label}
                </span>
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <Info label="Código" value={ticket.ticketId} />
                <Info label="Fecha" value={formatDate(ticket.eventDate)} />
                <Info label="Ubicación" value={ticket.eventAddress || 'Por confirmar'} />
                <Info label="Comprador" value={ticket.buyerName || 'Comprador'} />
                {ticket.buyerEmail ? <Info label="Email" value={ticket.buyerEmail} /> : null}
                {ticket.organizerName ? <Info label="Organizador" value={ticket.organizerName} /> : null}
                {ticket.orderId ? <Info label="Orden" value={ticket.orderId} /> : null}
                {ticket.paymentId ? <Info label="Pago MP" value={ticket.paymentId} /> : null}
              </div>

              <div className="mt-7 rounded-2xl border border-slate-200 bg-[#fafaf7] p-5">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Detalle de pago</p>
                <div className="mt-4 space-y-3 text-sm">
                  <Row label="Valor de la entrada" value={formatMoney(ticket.priceBreakdown?.entryValue)} />
                  <Row label="Cargo por servicio" value={formatMoney(ticket.priceBreakdown?.serviceFeeAmount)} />
                  <Row label="Total pagado" value={formatMoney(ticket.priceBreakdown?.totalPaid)} strong />
                </div>
              </div>

              {error ? <p className="mt-5 rounded-xl bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">{error}</p> : null}
              {canTryMarkUsed ? (
                <button type="button" onClick={handleMarkUsed} disabled={isMarking} className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-black px-5 py-3 text-sm font-black text-white transition hover:bg-[#536d12] disabled:cursor-wait disabled:bg-slate-500">
                  <CheckCircle className="h-4 w-4" />{isMarking ? 'Validando...' : 'Marcar entrada usada'}
                </button>
              ) : null}
            </>
          ) : null}
        </section>

        <aside className="h-fit rounded-[1.5rem] border border-slate-200 bg-white p-6 text-center shadow-[0_24px_54px_-40px_rgba(15,23,42,0.35)]">
          <div className="mx-auto grid h-64 w-64 place-items-center rounded-2xl border border-slate-200 bg-[#fafaf7] p-4">
            {ticket?.qrImageUrl ? (
              <img src={resolveImageUrl(ticket.qrImageUrl)} alt={`QR ${ticket.ticketId}`} className="h-full w-full object-contain" />
            ) : (
              <QrCode className="h-20 w-20 text-slate-400" />
            )}
          </div>
          <div className="mt-5 flex items-start justify-center gap-2 text-xs leading-5 text-slate-500">
            <Ticket className="mt-0.5 h-4 w-4 shrink-0 text-[#71931b]" />
            Presenta este QR en el acceso del evento.
          </div>
        </aside>
      </main>
    </div>
  );
};

const Info = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
    <p className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">{label}</p>
    <p className="mt-1 text-sm font-bold text-slate-800">{value}</p>
  </div>
);

const Row = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
  <div className={`flex justify-between gap-4 ${strong ? 'border-t border-slate-200 pt-3 text-base font-black' : ''}`}>
    <span className="text-slate-600">{label}</span>
    <span className={strong ? 'font-black' : 'font-bold'}>{value}</span>
  </div>
);

export default TicketView;
